import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Monitor, BarChart3, Boxes, Users, Printer, Cloud, Zap, ShieldCheck, Coffee, Store, Utensils, Wine, Scissors, Building2 } from "lucide-react";
import WaveBackground from "@/components/site/WaveBackground";
import BundleCTA from "@/components/site/BundleCTA";
import useSEO from "@/lib/useSEO";
import { IMG } from "@/lib/images";

const features = [
  { icon: Monitor, title: "Sleek touchscreen tills", desc: "Modern, intuitive hardware that staff pick up in minutes — not weeks of training." },
  { icon: BarChart3, title: "Live sales reporting", desc: "See what's selling, when, and by whom — from behind the counter or on the beach." },
  { icon: Boxes, title: "Stock & inventory built in", desc: "Track stock levels, get low-inventory alerts, and reorder in one click." },
  { icon: Users, title: "Staff & shift management", desc: "Clock in/out, track hours worked, and see who your top performers are." },
  { icon: Printer, title: "Kitchen & receipt printing", desc: "Split orders to kitchen printers automatically. No more shouted tickets." },
  { icon: Cloud, title: "Cloud-based & always on", desc: "Your data lives in the cloud — access it from any device, anywhere, any time." },
];

const industries = [
  { icon: Coffee, name: "Cafés & Coffee Shops", desc: "Fast counter service, table numbers, loyalty built in." },
  { icon: Utensils, name: "Restaurants", desc: "Order-at-table, kitchen tickets, split bills, tips." },
  { icon: Wine, name: "Pubs & Bars", desc: "Tabs, rounds, split bills, age-verification prompts." },
  { icon: Store, name: "Retail & Shops", desc: "Barcode scanning, stock control, customer accounts." },
  { icon: Scissors, name: "Salons & Barbers", desc: "Appointments, staff commission, product sales." },
  { icon: Building2, name: "Hospitality & More", desc: "From takeaways to garages — we spec the right kit." },
];

const bundle = [
  "Touchscreen till hardware",
  "Integrated card terminal",
  "Kitchen / bar printers",
  "Cash drawer & barcode scanner",
  "Cloud reporting dashboard",
  "Staff training & on-site setup",
];

export default function POSSystems() {
  useSEO({
    title: "POS Systems — Modern Point-of-Sale for UK Businesses | Fenco Digital",
    description: "Modern point-of-sale systems for cafés, restaurants, retail and more. Touchscreen tills, integrated card payments, stock control and cloud reporting — spec'd, sourced and set up by Fenco Digital.",
    path: "/pos-systems",
  });

  return (
    <div data-testid="pos-systems-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <WaveBackground variant="accent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Point-of-sale systems</p>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] text-gray-900 mt-5">
                Modern tills.<br />Zero faff<span className="text-[#FF5A1F]">.</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                A point-of-sale system that <strong className="text-gray-900">actually helps you run your business</strong> — not just take money. Touchscreen tills, integrated card payments, live stock, kitchen printing, and one dashboard that pulls it all together.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl" data-testid="pos-selling-points">
                {[
                  "All-in-one till hardware",
                  "Card payments built in",
                  "Live sales & stock",
                  "Cloud reporting anywhere",
                  "Staff & shift tracking",
                  "Set up on your go-live day",
                ].map((p) => (
                  <div key={p} className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-gray-700">{p}</span>
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="pos-cta-quote"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all duration-200 shadow-lg shadow-orange-500/25 hover:-translate-y-0.5"
                >
                  Get a POS quote <ArrowRight size={18} />
                </Link>
                <Link
                  to="/card-payments"
                  data-testid="pos-cta-cards"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-gray-900 bg-white border border-gray-200 hover:border-gray-300 rounded-full transition-all hover:-translate-y-0.5"
                >
                  See card payment solutions
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end fade-up">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-8 bg-[#FF5A1F]/15 blur-3xl rounded-full" />
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-gray-100">
                  <img
                    src={IMG.posHero}
                    alt="Modern café counter with a touchscreen point-of-sale system"
                    className="w-full h-full object-cover"
                    data-testid="pos-hero-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]" data-testid="pos-features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">What's included</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
              Everything a modern till<br />should do — and more.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`pos-feature-${i}`}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5">
                  <f.icon className="text-[#FF5A1F]" size={22} />
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900">{f.title}</h3>
                <p className="mt-3 text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE + INTEGRATION */}
      <section className="py-24 md:py-32 bg-white" data-testid="pos-integration-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-8 bg-[#FF5A1F]/10 blur-3xl rounded-full" />
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={IMG.posTerminal}
                    alt="A sleek touchscreen point-of-sale terminal on a shop counter"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="pos-terminal-image"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Card payments · integrated</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
                Till and card machine,<br />finally in sync.
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                Every POS system we set up integrates with a modern card terminal — so totals fly straight from till to card machine. No re-keying. No wrong amounts. No end-of-day headaches trying to match till Z-reports to your merchant statement.
              </p>

              <ul className="mt-8 space-y-3 text-gray-700">
                {[
                  "One-tap payments — total pushed from till to terminal",
                  "Split bills, add gratuity, take any card type",
                  "Refunds, voids and reports handled in the same place",
                  "Full audit trail — every transaction reconciled",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0" />
                    <span className="font-medium">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]" data-testid="pos-industries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Built for your sector</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
              POS that speaks<br />your business's language.
            </h2>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              A café doesn't need the same till as a barber shop. We spec, source and set up the right point-of-sale for the way your business actually trades.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((it, i) => (
              <div
                key={it.name}
                data-testid={`pos-industry-${i}`}
                className="bg-white rounded-2xl p-7 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
                    <it.icon size={20} className="text-[#FF5A1F]" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-gray-900">{it.name}</h3>
                </div>
                <p className="mt-4 text-gray-600 leading-relaxed text-sm">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S IN THE BUNDLE */}
      <section className="py-24 md:py-32 bg-white" data-testid="pos-bundle-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">A complete setup</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
                Everything you need,<br />on go-live day.
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                We deliver a proper end-to-end POS setup — hardware, software, connections, training. You unlock the door and start trading.
              </p>

              <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {bundle.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-gray-700">
                    <CheckCircle2 size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                    <span className="font-medium">{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link
                  to="/contact"
                  data-testid="pos-bundle-cta"
                  className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all shadow-lg shadow-orange-500/25 hover:-translate-y-0.5"
                >
                  Talk to us about a setup <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src={IMG.posRetail}
                    alt="Retail assistant using a tablet-based POS system"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="pos-retail-image"
                  />
                </div>
                <div className="space-y-4">
                  <div className="aspect-square rounded-3xl overflow-hidden shadow-xl">
                    <img
                      src={IMG.posContactless}
                      alt="Contactless payment being taken at a café counter"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      data-testid="pos-contactless-image"
                    />
                  </div>
                  <div className="aspect-square rounded-3xl overflow-hidden shadow-xl">
                    <img
                      src={IMG.posCounter}
                      alt="Modern point-of-sale station in a small business"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      data-testid="pos-counter-image"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] text-white relative overflow-hidden" data-testid="pos-how-section">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FF5A1F] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">How it works</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-4">
              From consultation to go-live in four steps.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              ["01", "Free consultation", "We visit or call to understand exactly how your business trades day-to-day."],
              ["02", "Spec the right kit", "We match you with the POS hardware, software and card terminal that fits."],
              ["03", "Delivery & install", "Hardware shipped, configured, and set up on-site — with your staff trained."],
              ["04", "We're with you on go-live", "First trading day, we're on-hand — remote or in person — so nothing wobbles."],
            ].map(([n, t, d], i) => (
              <div key={n} data-testid={`pos-step-${i}`} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition">
                <p className="font-display text-4xl font-extrabold text-[#FF5A1F]">{n}</p>
                <h3 className="font-display text-lg font-bold mt-4">{t}</h3>
                <p className="text-gray-300 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KEY BENEFITS BAND */}
      <section className="py-20 bg-white" data-testid="pos-benefits-band">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Zap, label: "Faster service", value: "Less time per order" },
              { icon: BarChart3, label: "Better decisions", value: "Data at your fingertips" },
              { icon: ShieldCheck, label: "Secure by default", value: "PCI compliant setup" },
              { icon: Cloud, label: "Access anywhere", value: "Web + mobile dashboard" },
            ].map((b, i) => (
              <div key={b.label} data-testid={`pos-benefit-band-${i}`} className="text-center p-5 bg-[#F8F9FA] rounded-2xl border border-gray-100">
                <b.icon className="mx-auto text-[#FF5A1F]" size={28} />
                <p className="mt-3 font-display text-base font-bold text-gray-900">{b.label}</p>
                <p className="mt-1 text-xs text-gray-500">{b.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUNDLE CROSS-SELL */}
      <BundleCTA />

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Monitor className="mx-auto text-[#FF5A1F] mb-6" size={36} />
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
            Time for a proper till<span className="text-[#FF5A1F]">?</span>
          </h2>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto">
            Tell us how your business trades and we'll spec the right point-of-sale — hardware, software, card payments and all. Zero obligation.
          </p>
          <Link
            to="/contact"
            data-testid="pos-final-cta"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/30"
          >
            Talk to us <ArrowRight size={18} />
          </Link>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-gray-400">
            {["Free consultation", "On-site setup", "Local NI team"].map((t) => (
              <span key={t} className="flex items-center gap-2 font-medium">
                <CheckCircle2 size={16} className="text-[#FF5A1F]" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
